import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  Input,
  Select,
  Flex,
  Text,
  Icon,
  Checkbox,
  Alert
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "../../../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import React, { useState, useEffect } from "react";
import {
  GetNewUserObject,
  GetUserRole,
  IsValidUser,
  impersonateUser,
  sendUserUpdate
} from "./Utils";
import { GetWSMgr } from "@/Utils/StateLib";
import { useFormController } from "@/hooks/useFormController";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList, enumMFAState } from "@coreapp/constants";
import { QMFAEmailSetupLink } from "@coreapp/CoreApp/QMFAEmailSetupLink";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { Globals } from "@coreapp/CoreApp/Globals";
import { kAdminUser, kRWUser, kROUser } from "@coreapp/constants";
import { getFeatureAccess } from "@/Utils/Misc";
import { ValidateEmailAddr } from "@coreapp/utils/ValidateEmailAddr";

export const EditUserModal = ({
  show,
  existingUser,
  accountConfig,
  groups,
  onClose,
  isCreateNewUser,
  showImpersonate,
  usersList
}) => {
  const [editUser, setEditUser] = useState(null);
  const [clonedUser, setClonedUser] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isNewUser, setIsNewUser] = useState(isCreateNewUser);
  const [changedFields, setChangedFields] = useState([]);
  const [mfaOverride, setmfaOverride] = useState(false);
  const [loading, setLoading] = useState(false);

  const dataTestIdPrefix = "edit-user-modal";
  const security = accountConfig?.security;
  const wsMgr = GetWSMgr();
  const cuName = wsMgr.GetUserName();
  const hasAdvSec = getFeatureAccess(enumFeaturesList.hasAdvSec);

  useEffect(() => {
    InitializeUser();
  }, []);

  const statusKeys = ["email", "username", "group", "role", "alert"];

  const getInitState = () => {
    return statusKeys.reduce((acc, key) => {
      acc[key] = {};
      return acc;
    }, {});
  };

  const formController = useFormController(() => getInitState());

  const resetStatusState = formController => {
    statusKeys.forEach(key => {
      formController.onFieldChange(key, {});
    });
  };

  const InitializeUser = () => {
    let initUser = null;
    if (existingUser) {
      initUser = existingUser;
    } else if (isNewUser) {
      initUser = GetNewUserObject(security);
      setIsNewUser(true);
    } else {
      return;
    }

    if (initUser != null) {
      setEditUser(initUser);
      setClonedUser(JSON.parse(JSON.stringify(initUser)));
      setmfaOverride((initUser["mfa"] || {})["backendMFAState"] == enumMFAState.overrideMFA);
    }
  };

  const GetUserDataObj = () => {
    let curUserID = editUser["username"];
    var theData = {};

    if (isNewUser) {
      theData["user"] = editUser;
      editUser["user"] = curUserID; // target user used for lookups in event name changes
    } else {
      theData["user"] = [];
      for (let key of changedFields) {
        if (key === "backendMFAState") {
          var theEntry = { mfa: {} };
          theEntry["mfa"][key] = editUser["mfa"][key];
          theData["user"].push(theEntry);
        } else {
          var theEntry = {};
          theEntry[key] = editUser[key];
          theData["user"].push(theEntry);
        }
      }
      theData["userID"] = clonedUser["username"]; // target user used for lookups in event name changes
    }
    return theData;
  };

  const onSaveUser = async () => {
    setLoading(true);
    resetStatusState(formController);
    const result = IsValidUser(editUser, clonedUser, usersList, groups);
    const errorFields = result.errorFields;
    const statusAlert = result.alert;
    if (errorFields.length > 0) {
      errorFields.forEach(errorField => {
        formController.onFieldChange(errorField.type, {
          status: "error",
          message: errorField.message
        });
      });
    }
    if (statusAlert.length > 0) {
      // throw status alert
      formController.onFieldChange("alert", {
        status: "error",
        message: result.alert,
        supportContact: result?.supportContact
      });
    }
    if (result.valid !== true) {
      setLoading(false);
      return;
    }

    const theData = GetUserDataObj();
    await sendUserUpdate({ editUser, isNewUser, theData, usersList, afterSaveHandler });
    setLoading(false);
  };

  const handleChange = (key, value) => {
    setEditUser({
      ...editUser,
      [key]: value
    });
    ChangedField(key);
  };

  const handleMFAChange = key => {
    let value;
    var usermfa = editUser["mfa"] || {};
    if (usermfa["backendMFAState"] == enumMFAState.overrideMFA) {
      var origUser = clonedUser;
      var origMFA = origUser["mfa"] || {};
      value =
        origMFA["backendMFAState"] != enumMFAState.overrideMFA
          ? origMFA["backendMFAState"]
          : enumMFAState.notConfigured;
    } else value = enumMFAState.overrideMFA;

    let updatedUser = editUser;
    updatedUser.mfa = { ...updatedUser.mfa, [key]: value };
    setEditUser(updatedUser);
    ChangedField(key);
    setmfaOverride((updatedUser["mfa"] || {})["backendMFAState"] == enumMFAState.overrideMFA);
  };
  const sendMFA = () => {
    const userInfo = wsMgr.GetUserInfo();
    var mfaRFC = new QMFAEmailSetupLink();
    const callbackObj = {
      EmailSetupLinkResult: (success, message) => {
        if (!message) return;
        const alert = new QAlert();
        alert.InitAlert(appstr("Alert"), message, null, wsMgr);
      }
    };
    mfaRFC.InitMFARFC(callbackObj);
    mfaRFC.SetParams(accountConfig?.orgname, userInfo.GetUserName(), editUser?.username);
    Globals._rfcMgr.QueueRFC(mfaRFC);
  };
  const ChangedField = key => {
    if (!changedFields.includes(key)) setChangedFields([...changedFields, key]);
  };

  const handleInputChange = e => {
    const value = e.target.value;
    const key = e.target.name;
    handleChange(key, value);
  };

  const afterSaveHandler = () => {
    setClonedUser(JSON.parse(JSON.stringify(editUser)));
    setIsDirty(false);
    onClose(true);
  };

  const onCancel = () => {
    if (isDirty) return;
    onClose(false);
  };

  const roleAssignedByGroup = editUser?.group && groups[editUser.group]?.roleassignment;

  const flexClass = "oc-settings-section-field";
  const contentClass = "oc-modal-content";

  let mfaActive = wsMgr.IsMFAActive(editUser);

  const mfaNotification = (mfastatus, user) => {
    let content;
    let curMFACode = wsMgr.GetMFAState(user, true);

    if (mfastatus) {
      content = (
        <>
          <Text
            colorModifier={"success"}
            element={"small"}
            modifier={"bold"}
            size={"xsmall"}
            type={"display"}
          >
            {appstr("Enabled")}
          </Text>
        </>
      );
    } else if (curMFACode === enumMFAState.overrideMFA) {
      content = (
        <>
          <Text
            colorModifier={"warning"}
            element={"small"}
            modifier={"bold"}
            size={"xsmall"}
            type={"display"}
          >
            {appstr("Bypassed")}
          </Text>
        </>
      );
    } else {
      content = (
        <>
          <Text
            colorModifier={"error"}
            element={"small"}
            modifier={"bold"}
            size={"xsmall"}
            type={"display"}
          >
            {appstr("Disabled")}
          </Text>
        </>
      );
    }
    return <>{content}</>;
  };

  const getFormData = key => {
    return formController.fieldData[key];
  };

  const usernameStatus = getFormData("username");
  const roleStatus = getFormData("role");
  const groupStatus = getFormData("group");
  const emailStatus = getFormData("email");
  const alertStatus = getFormData("alert");

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id={dataTestIdPrefix}
        show={show}
        minWidth={"medium"}
        maxWidth={"medium"}
        fillWidth={true}
        centerScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={() => onCancel()}
          data-testid="account-settings-manage-users-edit-user-close-btn"
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={isNewUser ? "user-plus" : "user-pen"} />
            {isNewUser ? appstr("Create new user") : appstr("Edit user")}
          </span>
        </DialogTitle>
        <DialogContent>
          <Flex direction={"column"} gap={"12px"} className={contentClass}>
            {alertStatus?.message?.length > 0 && (
              <Alert
                severity={alertStatus.status}
                className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
              >
                <span>
                  {alertStatus.message}
                  {alertStatus.supportContact && ValidateEmailAddr(alertStatus.supportContact) && (
                    <Button
                      size={"small"}
                      modifier={"link"}
                      onClick={() => {
                        window.location.href = `mailto:${alertStatus.supportContact}`;
                      }}
                    >
                      {`${alertStatus.supportContact}.`}
                    </Button>
                  )}
                  {alertStatus.supportContact &&
                    !ValidateEmailAddr(alertStatus.supportContact) &&
                    `${alertStatus.supportContact}.`}
                </span>
              </Alert>
            )}
            <Input
              label={appstr("Name")}
              name={"name"}
              placeholder={appstr("Name")}
              size={"medium"}
              value={editUser?.name}
              onChange={handleInputChange}
              id={dataTestIdPrefix + "-name-input"}
              dataTestId="account-settings-manage-users-edit-user-name-input"
            />
            <Input
              label={appstr("userid")}
              placeholder={appstr("userid")}
              name={"username"}
              value={editUser?.username}
              size={"medium"}
              onChange={handleInputChange}
              status={usernameStatus?.status}
              message={usernameStatus?.message}
              disabled={editUser?.username === cuName || editUser?.delegatedEmail}
              id={dataTestIdPrefix + "-username-input"}
              dataTestId="account-settings-manage-users-edit-user-userid-input"
              require={true}
            />
            <Input
              label={appstr("Email")}
              placeholder={appstr("Email")}
              name={"email"}
              value={
                editUser?.delegatedEmail
                  ? `${editUser.delegatedEmail} (${appstr("Delegated")})`
                  : (editUser?.email ?? "")
              }
              size={"medium"}
              onChange={handleInputChange}
              status={emailStatus?.status}
              message={emailStatus?.message}
              disabled={editUser?.delegatedEmail}
              id={dataTestIdPrefix + "-email-input"}
              dataTestId="account-settings-manage-users-edit-user-email-input"
            />
            <Flex justifyContent={"space-between"} gap={"24px"}>
              <Flex direction={"column"} gap={"8px"} className={flexClass} flex={"1"}>
                <Select
                  inputOnly={false}
                  label={appstr("Role")}
                  selectSize={"medium"}
                  disabled={roleAssignedByGroup || editUser?.username === cuName}
                  value={roleAssignedByGroup || editUser?.role}
                  status={roleStatus?.status}
                  message={roleStatus?.message}
                  options={BuildRoleOptions(handleChange, roleAssignedByGroup)}
                  dataTestId={dataTestIdPrefix + "-role-select"}
                  require={true}
                />
                <Checkbox
                  id={"usrmgr-override-chkbx"}
                  data-testid="account-settings-manage-users-edit-user-security-override-checkbox"
                  size={"medium"}
                  checked={editUser?.override}
                  label={appstr("securityoverride")}
                  onToggle={() => handleChange("override", !editUser?.override)}
                />
              </Flex>
              <Flex direction={"column"} gap={"8px"} className={flexClass} flex={"1"}>
                <Select
                  inputOnly={false}
                  label={appstr("group")}
                  selectSize={"medium"}
                  value={editUser?.group}
                  status={groupStatus?.status}
                  message={groupStatus?.message}
                  options={BuildGroupOptions(groups, handleChange)}
                  dataTestId={dataTestIdPrefix + "-group-select"}
                  require={true}
                />
              </Flex>
            </Flex>
            <Divider />
            <Flex direction="column" gap={"8px"}>
              <Flex direction={"row"} justifyContent="space-between">
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text size={"small"} modifier={"bold"}>
                    {appstr("MFA status")}
                  </Text>
                  {mfaNotification(mfaActive, editUser)}
                </Flex>
                <div style={{ width: "155px" }}>
                  <Button
                    size={"small"}
                    isFullWidth={true}
                    modifier={"secondary"}
                    iconProps={{ name: "envelope" }}
                    onClick={() => sendMFA()}
                    disabled={hasAdvSec !== true || isNewUser}
                    dataTestId="account-settings-manage-users-edit-user-email-mfa-btn"
                  >
                    {appstr("Email MFA setup link")}
                  </Button>
                </div>
              </Flex>
              <div style={{ marginBottom: "16px" }}>
                <Checkbox
                  id={"usrmgr-mfaoverride-chkbx"}
                  data-testid="account-settings-manage-users-edit-user-mfa-override-checkbox"
                  size={"medium"}
                  checked={mfaOverride}
                  label={appstr("overrideMFA")}
                  onToggle={() => handleMFAChange("backendMFAState")}
                  disabled={hasAdvSec !== true}
                />
              </div>
            </Flex>
            <div style={{ width: "155px" }}>
              <Button
                size={"small"}
                isFullWidth={true}
                modifier={"secondary"}
                iconProps={{ name: "user-play" }}
                onClick={() => impersonateUser(editUser)}
                disabled={!showImpersonate || isNewUser}
                dataTestId="account-settings-manage-users-edit-user-impersonate-btn"
              >
                {appstr("Impersonate user")}
              </Button>
            </div>
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            onClick={() => onCancel()}
            dataTestId={dataTestIdPrefix + "-cancel-button"}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            loading={loading}
            onClick={() => onSaveUser()}
            dataTestId={dataTestIdPrefix + "-save-button"}
          >
            {isNewUser ? appstr("Create") : appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

const BuildRoleOptions = (onClick, groupSelected = null) => {
  let options = [];
  for (let roleTypeCode of [kROUser, kRWUser, kAdminUser]) {
    let roleCode = GetUserRole(roleTypeCode);
    let option = {};
    option.label =
      roleTypeCode === groupSelected
        ? `${appstr(roleCode)} (${appstr("Group Assigned")})`
        : appstr(roleCode);
    option.name = "role";
    option.value = roleTypeCode;
    option.dataTestId = `user-role-${roleTypeCode}`;
    option.clickHandler = () => onClick(option.name, option.value);
    options.push(option);
  }
  return options;
};

const BuildGroupOptions = (groups, onClick) => {
  let options = [];
  if (!groups) return options;
  // we manually push this one, since it's not formally stored in groups
  let fullAccess = {
    label: appstr("fullaccess"),
    value: "0",
    name: "group",
    dataTestId: "user-group-fullaccess",
    clickHandler: () => onClick("group", "0")
  };
  options.push(fullAccess);
  for (let groupId in groups) {
    let option = {};
    let group = groups[groupId];
    option.label = group["name"];
    option.name = "group";
    option.value = groupId;
    option.dataTestId = `user-group-${group["name"]}`;
    option.clickHandler = () => onClick(option.name, option.value);
    options.push(option);
  }
  return options;
};
